import { useCallback, useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@/assets/styles/colors';
import { SCREEN_PREFERENCE_CODES } from '@/routes';
import {
  costCenterList,
  type CostCenterListItem,
  type CostCenterStatusFilter,
} from '@/api/domains/organization/costCenters';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import type { TableAction, TableColumn } from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useUI } from '@/hooks/ui/useUI';
import { SECTIONS } from '@/types/sections';
import { formatToDdMmYyyyHHmm } from '@/utils';
import { useCostCenterEnableDisable } from './useCostCenterEnableDisable';
import type { CostCenterFormRecord } from './useCostCenterFormPanel';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


interface CostCentersFiltersState {
  status: CostCenterStatusFilter;
}

interface UseCostCentersListResult {
  rows: CostCenterListItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: CostCentersFiltersState;
  setFilters: Dispatch<SetStateAction<CostCentersFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<CostCenterListItem>[];
  actions: TableAction<CostCenterListItem>[];
  onSearch: (term: string) => void;
  openCreatePanel: () => void;
  loading: boolean;
  refetch: () => void;
  debouncedSearchText: string;
}

export function useCostCentersList(): UseCostCentersListResult {
  const { t: translate } = useTranslation();
  const { openSidePanel, showApiError } = useUI();
  const { canSection } = usePagePermissions();
  const canCreateCostCenters = canSection(SECTIONS.CENTROS_COSTO, 'create');
  const canEditCostCenters = canSection(SECTIONS.CENTROS_COSTO, 'edit');
  const canToggleCostCenters = canSection(SECTIONS.CENTROS_COSTO, 'delete');
  const {
    enableCostCenter,
    disableCostCenter,
    enablingIds,
    disablingIds,
  } = useCostCenterEnableDisable();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<CostCentersFiltersState>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<CostCenterListItem> | null> => {
      const response = await costCenterList.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        status: params.filters.status,
      });
      if (!response.success || !response.data) {
        showApiError(response, translate('organization:messages.costCentersLoadError'));
        return null;
      }
      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    filters,
    setFilters,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<CostCenterListItem, CostCentersFiltersState>({
    fetchFn,
    initialFilters: { status: 'all' as CostCenterStatusFilter },
    screenCode: SCREEN_PREFERENCE_CODES.ORGANIZACION_COST_CENTERS,
  });

  const toggleCostCenterStatus = useCallback(async (row: CostCenterListItem) => {
    const success = row.isEnabled
      ? await disableCostCenter(row.id)
      : await enableCostCenter(row.id);

    if (success) {
      refetch();
    }
  }, [disableCostCenter, enableCostCenter, refetch]);

  useEffect(() => {
    const handleCostCenterFormSuccess = () => { refetch(); };
    window.addEventListener('cost-center-form-success', handleCostCenterFormSuccess);
    return () => {
      window.removeEventListener('cost-center-form-success', handleCostCenterFormSuccess);
    };
  }, [refetch]);

  // openCreatePanel: placeholder until side panel is implemented
  const openCostCenterPanel = useCallback((record: CostCenterFormRecord, title: string) => {
    openSidePanel({ title, component: 'CostCenterFormPanel', props: { record } });
  }, [openSidePanel]);

  const openCreatePanel = useCallback(() => {
    if (!canCreateCostCenters) return;
    openCostCenterPanel({ mode: 'create' }, translate('organization:panel.createCostCenterTitle'));
  }, [canCreateCostCenters, openCostCenterPanel, translate]);

  const openEditPanel = useCallback((row: CostCenterListItem) => {
    if (!canEditCostCenters) return;
    openCostCenterPanel({ mode: 'edit', costCenterId: row.id }, translate('organization:panel.editCostCenterTitle'));
  }, [canEditCostCenters, openCostCenterPanel, translate]);

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('organization:filters.status'),
      options: [
        { value: 'all', label: translate('organization:filters.all') },
        { value: 'enabled', label: translate('organization:filters.enabled') },
        { value: 'disabled', label: translate('organization:filters.disabled') },
      ],
    },
  ], [translate]);

  const columns: TableColumn<CostCenterListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('organization:fields.id'),
        sortable: true,
        render: (row: CostCenterListItem) => row.id,
        width: 80,
      },
      {
        key: 'code',
        header: translate('organization:fields.costCenterCode'),
        sortable: true,
        render: (row: CostCenterListItem) => row.code,
      },
      {
        key: 'name',
        header: translate('organization:fields.costCenterName'),
        sortable: true,
        render: (row: CostCenterListItem) => row.name,
      },
      {
        key: 'description',
        header: translate('organization:fields.description'),
        sortable: true,
        render: (row: CostCenterListItem) => row.description,
      },
      {
        key: 'createdAt',
        header: translate('common:fields.createdDate'),
        sortable: true,
        render: (row: CostCenterListItem) => formatToDdMmYyyyHHmm(row.createdAt) || '-',
      },
      {
        key: 'modifiedAt',
        header: translate('common:fields.modifiedDate'),
        sortable: true,
        render: (row: CostCenterListItem) => formatToDdMmYyyyHHmm(row.modifiedAt) || '-',
      },
      {
        key: 'status',
        header: translate('organization:fields.status'),
        sortable: true,
        render: (row: CostCenterListItem) => (
          <Text
            variant="body-regular"
            style={{
              color: row.isEnabled ? colors.feedback.success400 : colors.grays.neutral99,
              fontWeight: 500,
            }}
          >
            {row.isEnabled
              ? translate('organization:status.enabled')
              : translate('organization:status.disabled')}
          </Text>
        ),
      },
    ],
    [translate],
  );

  const actions: TableAction<CostCenterListItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEditCostCenters,
        onClick: (row: CostCenterListItem) => { openEditPanel(row); },
      },
      {
        id: 'enable',
        label: translate('common:tableActions.enable'),
        icon: 'check',
        hidden: (row: CostCenterListItem) => !canToggleCostCenters || row.isEnabled,
        disabled: (row: CostCenterListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        isLoading: (row: CostCenterListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        onClick: (row: CostCenterListItem) => {
          void toggleCostCenterStatus(row);
        },
      },
      {
        id: 'disable',
        label: translate('common:tableActions.disable'),
        icon: 'close',
        hidden: (row: CostCenterListItem) => !canToggleCostCenters || !row.isEnabled,
        disabled: (row: CostCenterListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        isLoading: (row: CostCenterListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        variant: 'danger',
        onClick: (row: CostCenterListItem) => {
          void toggleCostCenterStatus(row);
        },
      },
    ],
    [
      canEditCostCenters,
      canToggleCostCenters,
      disablingIds,
      enablingIds,
      openEditPanel,
      toggleCostCenterStatus,
      translate,
    ],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    openCreatePanel,
    loading,
    refetch,
    debouncedSearchText,
  };
}

export default useCostCentersList;
